import { useMemo, useContext, FC, useState, useEffect } from 'react';
import { Autocomplete, TextField } from '@mui/material';
import { useQuery } from 'react-query';
import { IDomainUser, IResponse } from '../../models';
import { getDomainUsers } from '../../fetch';
import { UserContext } from '../../context';

interface IInternalUsersDropdown {
  territoryId?: number;
  handleAssignToChange?: () => void;
  isEnabled?: boolean;
  selectedAssignee: IDomainUser | null;
  setSelectedAssignee: (val: IDomainUser | null) => void;
  setOptions?: (val: IDomainUser[]) => void;
}

export const InternalUsersDropdown: FC<IInternalUsersDropdown> = ({
  territoryId,
  handleAssignToChange,
  isEnabled,
  selectedAssignee,
  setSelectedAssignee,
  setOptions
}) => {
  const { isAdmin, isFieldAdmin, isField } = useContext(UserContext);
  const [assignToInput, setAssignToInput] = useState('');
  const [selectedInternalAssignee, setInternalSelectedAssignee] = useState<IDomainUser | null>(
    selectedAssignee?.displayName ? selectedAssignee : null
  );

  const { isLoading: isLoadingInternalUsers, data: internalUsersData } = useQuery<
    IResponse<IDomainUser[]>,
    Error
  >(
    ['domain-users'],
    () =>
      getDomainUsers({
        sortBy: 'firstName',
        territoryId: territoryId
      }),
    {
      onSuccess: d => setOptions?.(d.records),
      enabled: (isAdmin || isFieldAdmin || isField) && isEnabled
    }
  );

  const internalUsers: IDomainUser[] = useMemo(
    () => internalUsersData?.records ?? [],
    [internalUsersData]
  );

  useEffect(() => {
    setInternalSelectedAssignee(selectedAssignee);
  }, [setInternalSelectedAssignee, selectedAssignee, selectedInternalAssignee]);
  return (
    <Autocomplete
      id="assign-to"
      value={selectedInternalAssignee}
      onChange={(event, newValue: IDomainUser) => {
        handleAssignToChange && handleAssignToChange();
        setSelectedAssignee(newValue);
        setInternalSelectedAssignee(newValue);
      }}
      inputValue={assignToInput}
      onInputChange={(event, newInputValue) => {
        setAssignToInput(newInputValue);
      }}
      options={internalUsers}
      getOptionLabel={(option: any) => option?.displayName ?? option}
      filterSelectedOptions
      renderOption={(props, option: IDomainUser) => {
        return (
          <li {...props} key={`${option.accountName}`}>
            {option.displayName}
          </li>
        );
      }}
      renderInput={params => (
        <TextField
          {...params}
          disabled={isLoadingInternalUsers}
          label={isLoadingInternalUsers ? 'Loading...' : 'User'}
          size="small"
        />
      )}
    />
  );
};
